// components/Sidebar.js
import React from "react";
import { useNavigate, Link, NavLink } from "react-router-dom";
import authService from "../../Services/authService";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS, RoleId } from "../../types/auth";
import Logo from "../../assets/images/logo.svg";
import Dashboard from "../../assets/images/comman/sidebar/dashboard.svg";
import PatientsIcon from "../../assets/images/comman/sidebar/patients.svg";
import ClinicalServices from "../../assets/images/comman/sidebar/ClinicalServices.svg";
import Diagnostics from "../../assets/images/comman/sidebar/Diagnostics.svg";
import OPD from "../../assets/images/comman/sidebar/OPD.svg";
import AdmissionsIPD from "../../assets/images/comman/sidebar/AdmissionsIPD.svg";
import Billing from "../../assets/images/comman/sidebar/Billing.svg";
import Assessment from "../../assets/images/OPD-patients/Assessment.svg";

import Pharmacy from "../../assets/images/comman/sidebar/Pharmacy.svg";

const Sidebar = () => {
  const navigate = useNavigate();
  const { can, canAny, hasRole, user } = useAuth();

  const handleLogout = () => {
    authService.logoutWithNavigate(navigate);
  };

  // Nav registry with permission guards aligned with backend role matrix
  const NAV_ITEMS = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: <img src={Dashboard} alt="Dashboard" />,
      role: [1, 2, 3, 10, 11],
    },
    {
      path: "/patients-list",
      label: "Patients",
      icon: <img src={PatientsIcon} alt="Patients" />,
      permission: PERMISSIONS.PATIENTS_READ,
    },
    {
      path: "/patients",
      label: "OPD",
      icon: <img src={OPD} alt="OPD" />,
      role: [1, 2, 3, 4, 5, 8, 9, 10, 11],
      permission: PERMISSIONS.OPD_VIEW_QUEUE,
    },
    {
      path: "/pharmacy",
      label: "Pharmacy",
      icon: <img src={Pharmacy} alt="Pharmacy" />,
      role: [1, 2, 7],
      anyPermissions: [
        PERMISSIONS.PHARMACY_VIEW_ORDERS,
        PERMISSIONS.PHARMACY_DISPENSE,
        PERMISSIONS.CLINICAL_PRESCRIPTION_READ,
      ],
    },
    {
      path: "/billing",
      label: "Billing",
      icon: <img src={Billing} alt="Billing" />,
      role: [1, 2, 8, 9, 11],
      anyPermissions: [
        PERMISSIONS.BILLING_READ,
        PERMISSIONS.BILLING_READ_INVOICE,
      ],
    },
    {
      path: "/select-patient-assessment",
      label: "Assessment",
      icon: <img src={Assessment} alt="Assessment" />,
      role: [1, 3, 5, 10, 11],
      anyPermissions: [
        PERMISSIONS.CLINICAL_ASSESSMENT_READ,
        PERMISSIONS.CLINICAL_ASSESSMENT_WRITE,
      ],
    },
    {
      path: "/discharge-summary",
      label: "Discharge Summary",
      icon: (
        <i
          className="material-icons"
          style={{ color: "#0B1B8D", fontSize: "24px" }}
        >
          assignment
        </i>
      ),
      role: [1, 3, 5, 10, 11],
      anyPermissions: [
        PERMISSIONS.CLINICAL_DISCHARGE_READ,
        PERMISSIONS.CLINICAL_DISCHARGE_WRITE,
      ],
    },
    {
      path: "/consents",
      label: "Consent Request",
      icon: (
        <i
          className="material-icons"
          style={{ color: "#0B1B8D", fontSize: "24px" }}
        >
          description
        </i>
      ),
      role: [1, 2, 3, 5, 10],
      permission: PERMISSIONS.ABDM_CONSENT_READ,
    },
    {
      path: "/create-lab-report",
      label: "Lab Report",
      icon: <img src={AdmissionsIPD} alt="Lab Report" />,
      role: [1, 2, 3, 6, 10, 11],
      anyPermissions: [
        PERMISSIONS.LAB_READ,
        PERMISSIONS.LAB_READ_RESULTS,
        PERMISSIONS.LAB_ORDER,
        PERMISSIONS.LAB_ENTER_RESULTS,
      ],
    },
    {
      path: "/hospital-details",
      label: "Hospital Details",
      icon: <img src={ClinicalServices} alt="Hospital Details" />,
      role: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
      permission: PERMISSIONS.HOSPITAL_MANAGE,
    },
    {
      path: "/listing",
      label: "ABHA Listing",
      icon: (
        <i
          className="material-icons"
          style={{ color: "#0B1B8D", fontSize: "24px" }}
        >
          list
        </i>
      ),
      role: [1, 2, 3, 5, 6, 7, 9, 10, 11],
      permission: PERMISSIONS.PATIENTS_SEARCH,
    },
  ];

  // Dynamically filter links based on permissions and role
  const visibleLinks = NAV_ITEMS.filter((item) => {
    if (item.role && !hasRole(item.role)) {
      return false;
    }
    if (item.permission) {
      return can(item.permission);
    }
    if (item.anyPermissions && item.anyPermissions.length > 0) {
      return canAny(...item.anyPermissions);
    }
    return true;
  });

  return (
    <aside className="app-sidebar sticky">
      <div className="main-sidebar-header">
        <Link to="/dashboard">
          <img src={Logo} alt="Logo" width="100px" />
        </Link>
      </div>
      <div className="main-sidebar simplebar-scrollable-y">
        <nav className="main-menu-container nav nav-pills flex-column sub-open open active">
          <ul className="main-menu">
            {visibleLinks.map((link) => (
              <li key={link.path} className="slide has-sub">
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `side-menu-item ${isActive ? "active" : ""}`
                  }
                >
                  <span className="label-menu-icon">{link.icon}</span>
                  <span className="side-menu-label"> {link.label} </span>
                  <span className="right-arrow">
                    <i className="material-icons">keyboard_arrow_down</i>
                  </span>
                </NavLink>
              </li>
            ))}

            {/* Logout Button - Always shown at bottom for all authenticated users */}
            <li className="slide has-sub">
              <button
                onClick={handleLogout}
                className="side-menu-item"
                style={{
                  background: "none",
                  border: "none",
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 16px",
                  color: "#0B1B8D",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                  marginTop: "10px",
                }}
              >
                <span className="label-menu-icon">
                  <i
                    className="material-icons"
                    style={{
                      color: "#0B1B8D",
                      fontSize: "24px",
                      marginLeft: "8px",
                    }}
                  >
                    logout
                  </i>
                </span>
                <span className="side-menu-label"> Logout </span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
